import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Check } from 'lucide-react'

import { track } from '@/api/tracking'
import { buildAppOutboundUrl } from '@/components/food-cost/buildAppOutboundUrl'
import { FOOD_COST_LANDING_PAGE, OUTBOUND_TRACK_OPTIONS } from '@/components/food-cost/constants'
import { ENTRY_INTRO_FEE_EUR, ENTRY_LIST_FEE_EUR, getEntryIntroOfferEligibility } from '@tipi/shared'

import { formatOfferCountdownPart } from './leadFormOfferCountdown'
import { useLeadFormOfferCountdown } from './useLeadFormOfferCountdown'

export interface LeadFormOfferSuccessProps {
    leadId: string
    createdAt: string
    landingPage?: string
    mixpanelContext?: Record<string, unknown>
}

export function LeadFormOfferSuccess({
    leadId,
    createdAt,
    landingPage = FOOD_COST_LANDING_PAGE,
    mixpanelContext,
}: LeadFormOfferSuccessProps) {
    const { t, i18n } = useTranslation()
    const sectionRef = useRef<HTMLElement>(null)
    const { offerEndsAt } = getEntryIntroOfferEligibility({ createdAt })
    const remaining = useLeadFormOfferCountdown(offerEndsAt)
    const isExpired = remaining?.isExpired ?? true
    const signupUrl = buildAppOutboundUrl('post_lead_offer', {
        leadId,
        offerEndsAt: offerEndsAt.toISOString(),
    })
    const introFee = ENTRY_INTRO_FEE_EUR
    const listFee = ENTRY_LIST_FEE_EUR
    const deadlineLabel = offerEndsAt.toLocaleString(i18n.language.startsWith('en') ? 'en-GB' : 'es-ES', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })

    useEffect(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, [])

    const hours = formatOfferCountdownPart(remaining?.hours ?? 0)
    const minutes = formatOfferCountdownPart(remaining?.minutes ?? 0)
    const seconds = formatOfferCountdownPart(remaining?.seconds ?? 0)
    const progressPct = `${((remaining?.progressRatio ?? 0) * 100).toFixed(2)}%`

    return (
        <div className="flex justify-center px-4 py-8 font-['Archivo',system-ui,sans-serif] [@media(max-width:400px)]:px-4">
            <section
                ref={sectionRef}
                aria-label={t('leadForm.offer.sectionAria', 'Oferta válida hasta el {{deadline}}', {
                    deadline: deadlineLabel,
                })}
                className='flex w-full max-w-[520px] scroll-mt-24 flex-col gap-[22px] rounded-[28px] border border-[oklch(85%_0.03_80)] bg-[oklch(92%_0.03_88)] px-8 py-9 text-[oklch(28%_0.045_48)] [@media(max-width:400px)]:px-5 [@media(max-width:400px)]:py-7'
            >
                <div className='flex items-center gap-2.5 text-sm font-semibold'>
                    <span
                        className='flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[oklch(72%_0.17_148/0.25)] text-[oklch(48%_0.14_148)]'
                        aria-hidden
                    >
                        <Check className='size-[13px]' strokeWidth={2} />
                    </span>
                    <span>{t('leadForm.offer.confirmation', 'Recibido. Te escribimos en breve.')}</span>
                </div>

                <div className='flex flex-col gap-2.5'>
                    <div className='text-xs font-bold uppercase tracking-[0.14em] text-[oklch(62%_0.2_33)]'>
                        {isExpired
                            ? t('leadForm.offer.eyebrowExpired', 'Oferta caducada')
                            : t('leadForm.offer.eyebrow', 'Precio reservado para ti · solo 24 h')}
                    </div>
                    <div className='flex flex-wrap items-baseline gap-3'>
                        <span className='text-[30px] font-bold tracking-[-0.02em] text-[oklch(72%_0.02_75)] line-through'>
                            {t('leadForm.offer.listPrice', '{{price}} €', { price: listFee })}
                        </span>
                        <span className='text-[64px] font-extrabold leading-[0.98] tracking-[-0.035em] text-[oklch(18%_0.02_50)]'>
                            {t('leadForm.offer.introPrice', '{{price}} €', {
                                price: isExpired ? listFee : introFee,
                            })}
                        </span>
                        <span className='text-lg font-bold text-[oklch(62%_0.2_33)]'>
                            {t('leadForm.offer.vat', '+ IVA')}
                        </span>
                    </div>
                    <p className='m-0 text-pretty text-[15px] leading-[1.45] opacity-85'>
                        {isExpired
                            ? t(
                                  'leadForm.offer.bodyExpired',
                                  'Esta oferta ha caducado. El precio del primer mes vuelve a {{listPrice}} €.',
                                  { listPrice: listFee },
                              )
                            : t(
                                  'leadForm.offer.body',
                                  'Tu primer mes a {{introPrice}} € solo durante las próximas 24 horas. Después vuelve a {{listPrice}} €.',
                                  { introPrice: introFee, listPrice: listFee },
                              )}
                    </p>
                </div>

                <div
                    role='timer'
                    aria-live='off'
                    className='flex flex-col gap-3.5 rounded-[20px] bg-[oklch(97%_0.012_85)] px-5 py-[18px]'
                >
                    <div className='text-[11px] font-bold uppercase tracking-[0.14em] opacity-70'>
                        {t('leadForm.offer.countdownLabel', 'Caduca en')}
                    </div>
                    <div className='grid grid-cols-3 gap-2.5'>
                        <div className='flex flex-col gap-0.5'>
                            <span className='text-[40px] font-extrabold tabular-nums leading-none tracking-[-0.035em] text-[oklch(18%_0.02_50)]'>
                                {hours}
                            </span>
                            <span className='text-xs opacity-70'>{t('leadForm.offer.hours', 'horas')}</span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                            <span className='text-[40px] font-extrabold tabular-nums leading-none tracking-[-0.035em] text-[oklch(18%_0.02_50)]'>
                                {minutes}
                            </span>
                            <span className='text-xs opacity-70'>{t('leadForm.offer.minutes', 'minutos')}</span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                            <span className='text-[40px] font-extrabold tabular-nums leading-none tracking-[-0.035em] text-[oklch(62%_0.2_33)]'>
                                {seconds}
                            </span>
                            <span className='text-xs opacity-70'>{t('leadForm.offer.seconds', 'segundos')}</span>
                        </div>
                    </div>
                    <div className='h-1.5 overflow-hidden rounded-full bg-[oklch(88%_0.03_80)]'>
                        <div
                            className='h-full rounded-full bg-[oklch(62%_0.2_33)] transition-[width] duration-1000 ease-linear'
                            style={{ width: progressPct }}
                        />
                    </div>
                </div>

                <div className='flex flex-col items-center gap-3.5'>
                    <a
                        href={signupUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex h-12 w-full items-center justify-center rounded-full bg-[oklch(62%_0.2_33)] px-5 text-[15px] font-bold text-white no-underline transition-colors hover:bg-[oklch(58%_0.21_31)] focus-visible:shadow-[0_0_0_2px_#120c09,0_0_0_4px_oklch(62%_0.2_33)] focus-visible:outline-none'
                        onClick={() =>
                            track(
                                'Post Lead Offer CTA Clicked',
                                {
                                    landing_page: landingPage,
                                    cta: 'post_lead_offer',
                                    destination_url: signupUrl,
                                    offer_expired: isExpired,
                                    ...mixpanelContext,
                                },
                                OUTBOUND_TRACK_OPTIONS,
                            )
                        }
                    >
                        {isExpired
                            ? t('leadForm.offer.ctaExpired', 'Empiezo hoy por {{price}} €', { price: listFee })
                            : t('leadForm.offer.cta', 'Empiezo hoy por {{price}} €', { price: introFee })}
                    </a>
                    <p className='m-0 text-center text-[13px] opacity-75'>
                        {t('leadForm.offer.reassurance', '30 días con devolución del 100 %. Cancela cuando quieras.')}
                    </p>
                </div>
            </section>
        </div>
    )
}
